import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import {
    X, Search, File, Music, Image, Video, UploadCloud, Folder, FileText,
    Trash2, Copy, Check, AlertCircle, Loader2, CheckCircle, RefreshCw,
    CloudUpload, Download, ExternalLink, GripVertical, Pencil, Save, ChevronLeft
} from 'lucide-react';
import vaultService from '../../services/vaultService';
import AssetPreviewModal from './plan/AssetPreviewModal';
import { setDragState, clearDragState } from './dragState';
import { smartCopy } from '../../utils/textFormatting';

/**
 * VaultDrawer Component
 * 
 * A robust asset management system integrated with Firebase Cloud Storage.
 * Supports:
 * - Direct file uploads with drag-and-drop dropzone
 * - Canvas text snippet drag-and-drop
 * - Progress indicators for uploads
 * - Firestore metadata tracking
 * - Tenant-isolated storage paths: tenants/{tenantId}/vault/{assetType}/{filename}
 */
const VaultDrawer = ({ isOpen, onClose, user, episode, db }) => {
    // State management
    const [assets, setAssets] = useState([]);
    const [folders, setFolders] = useState([]);
    const [currentFolder, setCurrentFolder] = useState(null); // null = all assets
    const [stats, setStats] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isFileDragOver, setIsFileDragOver] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('episode'); // 'episode' | 'all'
    const [searchQuery, setSearchQuery] = useState('');
    const [draggedAssetId, setDraggedAssetId] = useState(null);

    // State for collapse behavior during drag
    const [isCollapsedForDrag, setIsCollapsedForDrag] = useState(false);
    const [isDraggingAsset, setIsDraggingAsset] = useState(false);
    const drawerRef = useRef(null);

    // State for inline name editing
    const [editingAssetId, setEditingAssetId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const [isSavingName, setIsSavingName] = useState(false);
    const nameInputRef = useRef(null);

    // Preview modal state
    const [previewAsset, setPreviewAsset] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    // Upload state
    const [uploadQueue, setUploadQueue] = useState([]);
    const fileInputRef = useRef(null);

    // Fetch assets when drawer opens or folder changes
    useEffect(() => {
        if (isOpen && user?.uid) {
            fetchAssets();
            fetchStats();
        }
    }, [isOpen, user?.uid, episode?.id, currentFolder]);

    /**
     * Fetch vault assets from Firestore
     */
    const fetchAssets = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const options = {};
            if (activeTab === 'episode' && episode?.id) {
                options.episodeId = episode.id;
            }
            if (currentFolder) {
                options.folder = currentFolder;
            }

            const result = await vaultService.listAssets(options);

            if (result.success) {
                setAssets(result.assets);
                if (result.folders) {
                    setFolders(result.folders);
                }
            }
        } catch (err) {
            console.error('Failed to fetch assets:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Fetch storage statistics
     */
    const fetchStats = async () => {
        try {
            const result = await vaultService.getStats();
            if (result.success) {
                setStats(result.stats);
            }
        } catch (err) {
            console.error('Failed to fetch stats:', err);
        }
    };

    /**
     * Handle file selection from input
     */
    const handleFileSelect = useCallback((e) => {
        const files = Array.from(e.target.files);
        files.forEach(file => handleFileUpload(file));
        e.target.value = ''; // Reset input
    }, [episode?.id]);

    /**
     * Handle file upload with progress tracking
     */
    const handleFileUpload = async (file) => {
        // Validate file
        const validation = vaultService.validateFile(file);
        if (!validation.valid) {
            setError(validation.error);
            return;
        }

        // Add to upload queue
        const uploadId = `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        setUploadQueue(prev => [...prev, {
            id: uploadId,
            fileName: file.name,
            progress: 0,
            status: 'uploading'
        }]);

        try {
            const result = await vaultService.uploadFile(file, {
                episodeId: episode?.id,
                onProgress: (progress) => {
                    setUploadQueue(prev => prev.map(item =>
                        item.id === uploadId ? { ...item, progress } : item
                    ));
                }
            });

            if (result.success) {
                // Update queue item to success
                setUploadQueue(prev => prev.map(item =>
                    item.id === uploadId ? { ...item, status: 'success' } : item
                ));

                // Add new asset to list
                setAssets(prev => [result.asset, ...prev]);

                // Update stats
                fetchStats();

                // Remove from queue after delay
                setTimeout(() => {
                    setUploadQueue(prev => prev.filter(item => item.id !== uploadId));
                }, 2000);
            }
        } catch (err) {
            console.error('Upload failed:', err);

            // Update queue item to error
            setUploadQueue(prev => prev.map(item =>
                item.id === uploadId ? { ...item, status: 'error', error: err.message } : item
            ));

            setError(err.message);

            // Remove from queue after delay
            setTimeout(() => {
                setUploadQueue(prev => prev.filter(item => item.id !== uploadId));
            }, 4000);
        }
    };

    /**
     * Handle text snippet drop from canvas
     */
    const handleTextSnippetUpload = async (content, title) => {
        // Add to upload queue
        const uploadId = `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        setUploadQueue(prev => [...prev, {
            id: uploadId,
            fileName: title || 'Text Snippet',
            progress: 50,
            status: 'uploading'
        }]);

        try {
            const result = await vaultService.uploadTextSnippet(content, title, episode?.id);

            if (result.success) {
                // Update queue item to success
                setUploadQueue(prev => prev.map(item =>
                    item.id === uploadId ? { ...item, status: 'success', progress: 100 } : item
                ));

                // Add new asset to list
                setAssets(prev => [result.asset, ...prev]);

                // Update stats
                fetchStats();

                // Remove from queue after delay
                setTimeout(() => {
                    setUploadQueue(prev => prev.filter(item => item.id !== uploadId));
                }, 2000);
            }
        } catch (err) {
            console.error('Text upload failed:', err);

            // Update queue item to error
            setUploadQueue(prev => prev.map(item =>
                item.id === uploadId ? { ...item, status: 'error', error: err.message } : item
            ));

            setError(err.message);

            // Remove from queue after delay
            setTimeout(() => {
                setUploadQueue(prev => prev.filter(item => item.id !== uploadId));
            }, 4000);
        }
    };

    /**
     * Handle drag over for file drop
     */
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';

        // Check if this is a file drag (from OS) or text drag (from canvas)
        if (e.dataTransfer.types.includes('Files') && !e.dataTransfer.types.includes('application/json')) {
            setIsFileDragOver(true);
        }
        setIsDragOver(true);
    }, []);

    /**
     * Handle drag leave
     */
    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        // Only reset if leaving the drawer entirely
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;
        if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
            setIsDragOver(false);
            setIsFileDragOver(false);
        }
    }, []);

    /**
     * Handle drop - supports both files and text snippets
     * Only handles drops when the drawer is NOT collapsed for drag (i.e., drops inside the drawer)
     * When collapsed, drops should go to the canvas
     */
    const handleDrop = useCallback((e) => {
        console.log('[VaultDrawer handleDrop] Event fired', {
            isCollapsedForDrag,
            dataTransferTypes: e.dataTransfer?.types,
            clientX: e.clientX,
            clientY: e.clientY
        });

        // If the drawer is collapsed for drag, don't intercept the drop
        // This allows the drop to propagate to the canvas/editor
        if (isCollapsedForDrag) {
            console.log('[VaultDrawer handleDrop] Drawer is collapsed, not intercepting drop');
            return;
        }

        e.preventDefault();
        setIsDragOver(false);
        setIsFileDragOver(false);

        // Handle file drops from OS
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            console.log('[VaultDrawer handleDrop] Files dropped from OS');
            const files = Array.from(e.dataTransfer.files);
            files.forEach(file => handleFileUpload(file));
            return;
        }

        // Handle JSON data (artifact from editor canvas - text snippets being saved to vault)
        // Note: vault-asset type should NOT be handled here when dragging out - it goes to canvas
        if (e.dataTransfer.types.includes('application/json')) {
            try {
                const jsonData = e.dataTransfer.getData('application/json');
                console.log('[VaultDrawer handleDrop] JSON data:', jsonData);
                const artifact = JSON.parse(jsonData);

                // If this is a vault-asset being dropped back into the vault, just refresh
                // This shouldn't normally happen when dragging OUT to canvas
                if (artifact && artifact.type === 'vault-asset') {
                    console.log('[VaultDrawer handleDrop] Vault asset dropped back into vault:', artifact.id);
                    fetchAssets();
                    return;
                }

                // Handle text snippets from canvas (not vault assets)
                if (artifact && artifact.content && !artifact.type) {
                    // Check if we already have this content (deduplication)
                    const isDuplicate = assets.some(existingAsset =>
                        existingAsset.contentPreview === artifact.content ||
                        (existingAsset.title === artifact.title && existingAsset.assetType === 'text')
                    );

                    if (!isDuplicate) {
                        // Use "Unnamed" as default title - user can edit after saving
                        handleTextSnippetUpload(artifact.content, 'Unnamed');
                    } else {
                        console.log('[VaultDrawer handleDrop] Duplicate artifact detected, skipping creation');
                    }
                }
            } catch (err) {
                console.error('[VaultDrawer handleDrop] Failed to parse dropped artifact:', err);
                setError('Failed to process dropped content');
            }
            return;
        }

        // Handle plain text drops
        if (e.dataTransfer.types.includes('text/plain')) {
            const text = e.dataTransfer.getData('text/plain');
            console.log('[VaultDrawer handleDrop] Plain text dropped:', text?.substring(0, 100));
            if (text.trim()) {
                // Check for duplicates
                const isDuplicate = assets.some(existingAsset =>
                    existingAsset.contentPreview === text ||
                    existingAsset.title === text.slice(0, 50)
                );

                if (!isDuplicate) {
                    // Use "Unnamed" as default title - user can edit after saving
                    handleTextSnippetUpload(text, 'Unnamed');
                }
            }
        }
    }, [episode?.id, assets, isCollapsedForDrag]);

    /**
     * Delete an asset
     */
    const handleDeleteAsset = async (assetId) => {
        try {
            await vaultService.deleteAsset(assetId);
            setAssets(prev => prev.filter(a => a.id !== assetId));
            fetchStats();
        } catch (err) {
            console.error('Failed to delete asset:', err);
            setError(err.message);
        }
    };

    /**
     * Copy asset content or URL
     */
    const handleCopy = async (asset) => {
        try {
            let textToCopy = '';

            if (asset.assetType === 'text' || asset.assetType === 'document') {
                // For text types, we want the content, not the URL
                if (asset.content) {
                    textToCopy = asset.content;
                } else if (asset.downloadUrl) {
                    try {
                        const response = await fetch(asset.downloadUrl);
                        if (response.ok) {
                            textToCopy = await response.text();
                        } else {
                            textToCopy = asset.contentPreview || asset.downloadUrl;
                        }
                    } catch (e) {
                        console.warn('Could not fetch long content for copy:', e.message);
                        textToCopy = asset.contentPreview || asset.downloadUrl;
                    }
                } else {
                    textToCopy = asset.contentPreview || '';
                }
            } else {
                // For images, videos, audio, just copy the URL
                textToCopy = asset.downloadUrl || asset.contentPreview || '';
            }

            // Vault doesn't know the exact platform, so we do a smart 'raw' copy
            await smartCopy(textToCopy);
            setCopiedId(asset.id);
            setTimeout(() => setCopiedId(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    /**
     * Open asset preview modal
     */
    const handlePreviewAsset = (asset) => {
        setPreviewAsset(asset);
        setIsPreviewOpen(true);
    };

    /**
     * Close asset preview modal
     */
    const handleClosePreview = () => {
        setIsPreviewOpen(false);
        setPreviewAsset(null);
    };

    /**
     * Start editing an asset's name
     */
    const handleStartEditName = useCallback((e, asset) => {
        e.stopPropagation();
        setEditingAssetId(asset.id);
        setEditingName(asset.fileName || asset.title || 'Unnamed');
        // Focus the input after a short delay to ensure it's rendered
        setTimeout(() => {
            nameInputRef.current?.focus();
            nameInputRef.current?.select();
        }, 50);
    }, []);

    /**
     * Cancel editing name
     */
    const handleCancelEditName = useCallback(() => {
        setEditingAssetId(null);
        setEditingName('');
    }, []);

    /**
     * Save the edited name
     */
    const handleSaveName = useCallback(async (assetId) => {
        if (!editingName.trim()) {
            handleCancelEditName();
            return;
        }

        setIsSavingName(true);
        try {
            // Update the asset name via API
            const result = await vaultService.updateAssetName(assetId, editingName.trim());

            if (result.success) {
                // Update local state
                setAssets(prev => prev.map(asset =>
                    asset.id === assetId
                        ? { ...asset, fileName: editingName.trim(), title: editingName.trim() }
                        : asset
                ));
            }
        } catch (err) {
            console.error('Failed to update asset name:', err);
            setError('Failed to rename asset');
        } finally {
            setIsSavingName(false);
            setEditingAssetId(null);
            setEditingName('');
        }
    }, [editingName, handleCancelEditName]);

    /**
     * Handle drag start for asset items - enables dragging to canvas
     */
    const handleAssetDragStart = useCallback((e, asset) => {
        setDraggedAssetId(asset.id);
        setIsDraggingAsset(true);

        // Estimate element dimensions based on asset type for auto-scroll triggering
        // These are approximate sizes for the drag preview/ghost element
        const estimatedDimensions = {
            image: { width: 400, height: 300 },
            video: { width: 400, height: 225 },
            audio: { width: 300, height: 80 },
            document: { width: 300, height: 100 },
            text: { width: 300, height: 120 }
        };

        const dimensions = estimatedDimensions[asset.assetType] || { width: 300, height: 150 };

        // Set drag state for edge-based auto-scroll in SmartEditor
        // This is needed because dataTransfer.getData() is not accessible during dragover
        setDragState({
            width: dimensions.width,
            height: dimensions.height,
            type: 'vault-asset',
            assetType: asset.assetType
        });

        // Create artifact data for canvas drop
        const artifactData = {
            id: asset.id,
            type: 'vault-asset',
            assetType: asset.assetType,
            mimeType: asset.mimeType, // Include MIME type for PDF detection
            fileName: asset.fileName || asset.title,
            title: asset.fileName || asset.title,
            downloadUrl: asset.downloadUrl,
            contentPreview: asset.contentPreview,
            size: asset.size,
            createdAt: asset.createdAt,
            // For text snippets, include the FULL content (not truncated preview)
            content: asset.content || asset.contentPreview || null,
            // Thumbnail for images
            thumbnailUrl: asset.assetType === 'image' ? asset.downloadUrl : null,
            // Estimated dimensions for auto-scroll calculation
            dragWidth: dimensions.width,
            dragHeight: dimensions.height,
            // For PDFs, include pre-extracted text (cached on upload)
            extractedText: asset.extractedText || null,
            pdfPageCount: asset.pdfPageCount || null
        };

        // Set both JSON and plain text data
        e.dataTransfer.setData('application/json', JSON.stringify(artifactData));
        e.dataTransfer.setData('text/plain', asset.downloadUrl || asset.contentPreview || asset.fileName || '');
        e.dataTransfer.effectAllowed = 'copy';

        // Create custom drag image
        const dragPreview = document.createElement('div');
        dragPreview.className = 'vault-drag-preview';
        dragPreview.innerHTML = `
            <div style="
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 12px;
                background: white;
                border-radius: 8px;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                font-family: system-ui, -apple-system, sans-serif;
                font-size: 12px;
                font-weight: 600;
                color: #334155;
            ">
                <span style="color: #6366f1;">📄</span>
                <span>${asset.fileName || asset.title}</span>
            </div>
        `;
        dragPreview.style.position = 'absolute';
        dragPreview.style.top = '-1000px';
        document.body.appendChild(dragPreview);
        e.dataTransfer.setDragImage(dragPreview, 0, 0);

        // Clean up drag preview after drag starts
        setTimeout(() => {
            document.body.removeChild(dragPreview);
        }, 0);
    }, []);

    /**
     * Handle drag end for asset items
     */
    const handleAssetDragEnd = useCallback(() => {
        setDraggedAssetId(null);
        setIsDraggingAsset(false);
        setIsCollapsedForDrag(false);
        // Clear drag state for edge-based auto-scroll
        clearDragState();
    }, []);

    /**
     * Global drag tracking for collapse behavior
     * Collapses drawer when dragging asset toward canvas
     */
    useEffect(() => {
        if (!isDraggingAsset) return;

        const handleGlobalDragOver = (e) => {
            if (!drawerRef.current) return;

            const drawerRect = drawerRef.current.getBoundingClientRect();
            const mouseX = e.clientX;

            // Calculate distance from drawer's left edge
            // The drawer is on the right side, so left edge is the boundary with canvas
            const distanceFromDrawer = mouseX - drawerRect.left;

            // If mouse is more than 50px into the canvas area (left of drawer), collapse
            // This gives a smooth transition as user drags away from drawer
            if (distanceFromDrawer < -50) {
                setIsCollapsedForDrag(true);
            } else if (distanceFromDrawer > 0) {
                // Mouse is back inside drawer area, expand
                setIsCollapsedForDrag(false);
            }
        };

        const handleGlobalDragEnd = () => {
            setIsDraggingAsset(false);
            setIsCollapsedForDrag(false);
        };

        // Note: We don't add a global drop handler here because it would interfere
        // with the editor's drop handling. The dragend event is sufficient for cleanup.

        document.addEventListener('dragover', handleGlobalDragOver);
        document.addEventListener('dragend', handleGlobalDragEnd);

        return () => {
            document.removeEventListener('dragover', handleGlobalDragOver);
            document.removeEventListener('dragend', handleGlobalDragEnd);
        };
    }, [isDraggingAsset]);

    /**
     * Format date for display
     * Handles Firestore Timestamp objects, serialized timestamps, ISO strings, and Date objects
     */
    const formatDate = (timestamp) => {
        if (!timestamp) return '';

        let date;

        // Handle Firestore Timestamp object (has toDate method)
        if (timestamp && typeof timestamp.toDate === 'function') {
            date = timestamp.toDate();
        }
        // Handle serialized Firestore timestamp (has _seconds property)
        else if (timestamp && typeof timestamp._seconds === 'number') {
            date = new Date(timestamp._seconds * 1000);
        }
        // Handle ISO string or existing Date object
        else if (typeof timestamp === 'string' || timestamp instanceof Date) {
            date = new Date(timestamp);
        }
        // Handle numeric timestamp (milliseconds)
        else if (typeof timestamp === 'number') {
            date = new Date(timestamp);
        }
        else {
            return '';
        }

        // Validate the date is valid before formatting
        if (isNaN(date.getTime())) {
            return '';
        }

        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    /**
     * Get icon component for asset type
     */
    const getAssetIcon = (assetType) => {
        const iconProps = { size: 18 };
        switch (assetType) {
            case 'audio':
                return <Music {...iconProps} className="text-purple-500" />;
            case 'video':
                return <Video {...iconProps} className="text-red-500" />;
            case 'image':
                return <Image {...iconProps} className="text-green-500" />;
            case 'document':
                return <FileText {...iconProps} className="text-blue-500" />;
            case 'text':
                return <FileText {...iconProps} className="text-slate-500" />;
            default:
                return <File {...iconProps} className="text-slate-400" />;
        }
    };

    /**
     * Filter assets by search query
     * Also filter out any assets with empty/invalid IDs to prevent React key warnings
     */
    const filteredAssets = assets.filter(asset => {
        // Filter out assets without valid IDs
        if (!asset.id || (typeof asset.id === 'string' && asset.id.trim() === '')) {
            console.warn('Filtered out asset with invalid ID:', asset);
            return false;
        }

        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            asset.fileName?.toLowerCase().includes(query) ||
            asset.title?.toLowerCase().includes(query) ||
            asset.description?.toLowerCase().includes(query) ||
            asset.tags?.some(tag => tag.toLowerCase().includes(query))
        );
    });

    /**
     * Calculate storage percentage
     */
    const storagePercentage = stats ? Math.min(Math.round((stats.totalSize / (1024 * 1024 * 1024)) * 100), 100) : 0;

    // Portal content - renders at document body level
    const drawerContent = (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop - hide when collapsed for drag and don't intercept events */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: isCollapsedForDrag ? 0 : 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className={`fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-[9998] ${isCollapsedForDrag ? 'pointer-events-none' : ''}`}
                    />

                    {/* Drawer */}
                    <motion.div
                        ref={drawerRef}
                        initial={{ x: '100%' }}
                        animate={{
                            x: isCollapsedForDrag ? '100%' : 0,
                            opacity: isCollapsedForDrag ? 0.3 : 1,
                            scale: isCollapsedForDrag ? 0.95 : 1
                        }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        className={`fixed top-0 right-0 h-full w-full sm:w-[450px] bg-white border-l-2 shadow-2xl z-[9999] flex flex-col transition-colors ${isDragOver ? 'border-l-indigo-400 bg-indigo-50/30' : 'border-l-slate-100'
                            } ${isCollapsedForDrag ? 'pointer-events-none' : ''}`}
                    >
                        {/* Header */}
                        <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                            <div className="flex items-center gap-3">
                                {currentFolder ? (
                                    <button
                                        onClick={() => setCurrentFolder(null)}
                                        className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-900 transition-colors"
                                    >
                                        <ChevronLeft size={18} />
                                    </button>
                                ) : null}
                                <div className={`p-2 rounded-xl transition-colors ${isDragOver ? 'bg-indigo-200 text-indigo-700' : 'bg-indigo-100 text-indigo-600'
                                    }`}>
                                    <Folder size={20} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900 font-['Outfit']">
                                        {currentFolder || 'The Vault'}
                                    </h3>
                                    <p className="text-xs text-slate-500 font-medium">
                                        {isDragOver ? 'Drop to save asset' : currentFolder ? `${currentFolder} folder` : 'Project Assets & Library'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-900 transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Drop Zone Overlay */}
                        <AnimatePresence>
                            {isDragOver && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="absolute inset-0 bg-indigo-500/10 backdrop-blur-sm z-10 flex items-center justify-center pointer-events-none"
                                >
                                    <div className="bg-white rounded-2xl p-8 shadow-2xl border-2 border-dashed border-indigo-400 text-center">
                                        <CloudUpload size={48} className="mx-auto text-indigo-500 mb-4" />
                                        <p className="text-lg font-bold text-indigo-600">
                                            {isFileDragOver ? 'Drop files to upload' : 'Drop to save snippet'}
                                        </p>
                                        <p className="text-sm text-slate-500 mt-1">
                                            Audio, Video, Images, Text files supported
                                        </p>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Error Alert */}
                        <AnimatePresence>
                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="px-4 pt-3"
                                >
                                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                                        <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <p className="text-sm text-red-700">{error}</p>
                                        </div>
                                        <button
                                            onClick={() => setError(null)}
                                            className="text-red-400 hover:text-red-600"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Upload Queue */}
                        <AnimatePresence>
                            {uploadQueue.length > 0 && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="px-4 pt-3"
                                >
                                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
                                        {uploadQueue.map((item) => (
                                            <div key={item.id} className="flex items-center gap-3">
                                                {item.status === 'uploading' && (
                                                    <Loader2 size={16} className="text-indigo-500 animate-spin" />
                                                )}
                                                {item.status === 'success' && (
                                                    <CheckCircle size={16} className="text-green-500" />
                                                )}
                                                {item.status === 'error' && (
                                                    <AlertCircle size={16} className="text-red-500" />
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-slate-700 truncate">
                                                        {item.fileName}
                                                    </p>
                                                    {item.status === 'uploading' && (
                                                        <div className="w-full h-1.5 bg-slate-200 rounded-full mt-1 overflow-hidden">
                                                            <div
                                                                className="h-full bg-indigo-500 rounded-full transition-all duration-300"
                                                                style={{ width: `${item.progress}%` }}
                                                            />
                                                        </div>
                                                    )}
                                                    {item.status === 'error' && (
                                                        <p className="text-xs text-red-500">{item.error}</p>
                                                    )}
                                                </div>
                                                <span className="text-xs text-slate-400">
                                                    {item.status === 'uploading' ? `${item.progress}%` : ''}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Search & Upload */}
                        <div className="p-4 space-y-3">
                            <div className="relative">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search assets..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2.5 text-sm font-medium focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 outline-none transition-all placeholder:text-slate-400"
                                />
                            </div>

                            {/* Upload Button / Dropzone */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                onChange={handleFileSelect}
                                className="hidden"
                                accept="audio/*,video/*,image/*,text/*,.pdf,.doc,.docx"
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full py-3 bg-white border-2 border-dashed border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50/50 rounded-xl text-indigo-600 text-sm font-bold uppercase tracking-wide flex items-center justify-center gap-2 transition-all"
                            >
                                <UploadCloud size={16} />
                                Upload Files
                            </button>
                        </div>

                        {/* Tabs */}
                        <div className="flex items-center px-4 border-b border-slate-100">
                            <button
                                onClick={() => { setActiveTab('episode'); fetchAssets(); }}
                                className={`flex-1 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'episode'
                                    ? 'text-indigo-600 border-indigo-600'
                                    : 'text-slate-400 hover:text-slate-600 border-transparent'
                                    }`}
                            >
                                This Episode
                            </button>
                            <button
                                onClick={() => { setActiveTab('all'); fetchAssets(); }}
                                className={`flex-1 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'all'
                                    ? 'text-indigo-600 border-indigo-600'
                                    : 'text-slate-400 hover:text-slate-600 border-transparent'
                                    }`}
                            >
                                All Assets
                            </button>
                        </div>

                        {/* Folder Navigation */}
                        {folders.length > 0 && (
                            <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/50">
                                <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
                                    <span className="text-xs text-slate-500 font-medium flex-shrink-0">Folders:</span>
                                    <button
                                        onClick={() => setCurrentFolder(null)}
                                        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all flex-shrink-0 ${!currentFolder
                                            ? 'bg-indigo-100 text-indigo-700'
                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                            }`}
                                    >
                                        All
                                    </button>
                                    {folders.map((folder) => (
                                        <button
                                            key={folder}
                                            onClick={() => setCurrentFolder(folder)}
                                            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all flex-shrink-0 ${currentFolder === folder
                                                ? 'bg-indigo-100 text-indigo-700'
                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}
                                        >
                                            {folder}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Content Grid */}
                        <div className="flex-1 overflow-y-auto p-4">
                            {/* Loading State */}
                            {isLoading && (
                                <div className="flex items-center justify-center h-32">
                                    <Loader2 size={24} className="text-indigo-500 animate-spin" />
                                </div>
                            )}

                            {/* Assets List */}
                            {!isLoading && filteredAssets.length > 0 && (
                                <div className="space-y-2">
                                    {filteredAssets.map((asset, index) => (
                                        <div
                                            key={asset.id}
                                            draggable
                                            onDragStart={(e) => handleAssetDragStart(e, asset)}
                                            onDragEnd={handleAssetDragEnd}
                                            onClick={() => handlePreviewAsset(asset)}
                                            className={`bg-white border p-3 rounded-xl transition-all group cursor-grab active:cursor-grabbing relative ${draggedAssetId === asset.id
                                                ? 'border-indigo-400 shadow-lg shadow-indigo-100 opacity-60 scale-[0.98]'
                                                : 'border-slate-100 hover:border-indigo-200 hover:shadow-sm'
                                                }`}
                                        >
                                            <div className="flex items-start gap-3">
                                                {/* Drag Handle Indicator */}
                                                <div className="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-40 transition-opacity">
                                                    <GripVertical size={12} className="text-slate-400" />
                                                </div>
                                                <div className="p-2 bg-slate-50 rounded-lg">
                                                    {getAssetIcon(asset.assetType)}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-start justify-between gap-2">
                                                        {/* Inline name editing */}
                                                        {editingAssetId === asset.id ? (
                                                            <div className="flex items-center gap-1 flex-1" onClick={(e) => e.stopPropagation()}>
                                                                <input
                                                                    ref={nameInputRef}
                                                                    type="text"
                                                                    value={editingName}
                                                                    onChange={(e) => setEditingName(e.target.value)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            handleSaveName(asset.id);
                                                                        } else if (e.key === 'Escape') {
                                                                            handleCancelEditName();
                                                                        }
                                                                    }}
                                                                    className="flex-1 px-2 py-1 text-sm font-bold text-slate-700 border border-indigo-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                                                    placeholder="Enter name..."
                                                                />
                                                                <button
                                                                    onClick={() => handleSaveName(asset.id)}
                                                                    disabled={isSavingName}
                                                                    className="p-1 hover:bg-green-50 rounded-md text-green-600 hover:text-green-700"
                                                                    title="Save"
                                                                >
                                                                    {isSavingName ? (
                                                                        <Loader2 size={14} className="animate-spin" />
                                                                    ) : (
                                                                        <Save size={14} />
                                                                    )}
                                                                </button>
                                                                <button
                                                                    onClick={handleCancelEditName}
                                                                    className="p-1 hover:bg-slate-100 rounded-md text-slate-400 hover:text-slate-600"
                                                                    title="Cancel"
                                                                >
                                                                    <X size={14} />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <h4 className="text-sm font-bold text-slate-700 truncate">
                                                                {asset.fileName || asset.title || 'Unnamed'}
                                                            </h4>
                                                        )}
                                                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                                                            {/* Edit name button */}
                                                            <button
                                                                onClick={(e) => handleStartEditName(e, asset)}
                                                                className="p-1.5 hover:bg-indigo-50 rounded-md text-slate-400 hover:text-indigo-600"
                                                                title="Rename"
                                                            >
                                                                <Pencil size={12} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleCopy(asset)}
                                                                className="p-1.5 hover:bg-slate-100 rounded-md text-slate-400 hover:text-slate-600"
                                                                title={asset.assetType === 'text' || asset.assetType === 'document' ? 'Copy Text' : 'Copy URL'}
                                                            >
                                                                {copiedId === asset.id ? (
                                                                    <Check size={12} className="text-green-600" />
                                                                ) : (
                                                                    <Copy size={12} />
                                                                )}
                                                            </button>
                                                            {asset.downloadUrl && (
                                                                <a
                                                                    href={asset.downloadUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="p-1.5 hover:bg-slate-100 rounded-md text-slate-400 hover:text-slate-600"
                                                                    title="Open"
                                                                >
                                                                    <ExternalLink size={12} />
                                                                </a>
                                                            )}
                                                            <button
                                                                onClick={() => handleDeleteAsset(asset.id)}
                                                                className="p-1.5 hover:bg-red-50 rounded-md text-slate-400 hover:text-red-500"
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={12} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-[10px] text-slate-400 uppercase font-medium">
                                                            {asset.assetType}
                                                        </span>
                                                        <span className="text-[10px] text-slate-300">•</span>
                                                        <span className="text-[10px] text-slate-400">
                                                            {vaultService.formatFileSize(asset.size)}
                                                        </span>
                                                    </div>
                                                    {asset.contentPreview && (
                                                        <p className="text-[10px] text-slate-400 line-clamp-2 mt-1">
                                                            {asset.contentPreview}
                                                        </p>
                                                    )}
                                                    <p className="text-[9px] text-slate-300 mt-1">
                                                        {formatDate(asset.createdAt)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Empty State */}
                            {!isLoading && filteredAssets.length === 0 && (
                                <div className="flex flex-col items-center justify-center h-48 text-center text-slate-400">
                                    <Folder size={32} className="mb-2 opacity-50" />
                                    <p className="text-sm font-medium">Vault is empty</p>
                                    <p className="text-xs mt-1">Drag files or text snippets here</p>
                                    <p className="text-xs text-slate-300 mt-0.5">or use the upload button above</p>
                                </div>
                            )}
                        </div>

                        {/* Capacity Indicator */}
                        <div className="p-4 bg-slate-50 border-t border-slate-100">
                            <div className="flex justify-between text-[10px] font-bold text-slate-500 mb-1">
                                <span>Storage Used</span>
                                <span>{stats ? vaultService.formatFileSize(stats.totalSize) : '0 KB'}</span>
                            </div>
                            <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-indigo-500 rounded-full transition-all duration-500"
                                    style={{ width: `${storagePercentage}%` }}
                                />
                            </div>
                            <div className="flex justify-between text-[9px] text-slate-400 mt-1">
                                <span>{stats?.totalAssets || 0} assets</span>
                                <span>1 GB limit</span>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}

            {/* Asset Preview Modal */}
            <AssetPreviewModal
                isOpen={isPreviewOpen}
                onClose={handleClosePreview}
                asset={previewAsset}
            />
        </AnimatePresence>
    );

    // Use portal to render at document body level to escape modal stacking context
    return createPortal(drawerContent, document.body);
};

export default VaultDrawer;
